import OrgNodeCard from './OrgNodeCard';
import { buildPrintSections } from './orgChartSections';
import './orgChart.css';

/**
 * The printable org chart: one section per manager, each a header naming the
 * manager followed by their direct reports as a wrapping row of cards.
 *
 * This is a SEPARATE view from the on-screen tree, deliberately. The tree's
 * connector lines are CSS pseudo elements on a nested ul/li structure whose
 * width grows with the widest sibling row, which is exactly what cannot be
 * printed: print media clips overflow instead of scrolling, so the rightmost
 * branches of a wide org vanish with nothing on the page to say so. Sections
 * never exceed the page width whatever the org's shape.
 *
 * Rendered off-screen at all times and revealed only by the print stylesheet,
 * so it never disturbs the interactive page.
 */
export default function OrgPrintView({
  roots, isAdmin = false, title, caption, printedOn = '',
}) {
  const sections = buildPrintSections(roots);

  return (
    <div className="oc-print-doc" aria-hidden="true">
      {/* RUNNING header and footer: position:fixed is what makes a browser repeat
          an element on every printed page. CSS @page margin boxes would be the
          textbook answer, but no browser supports images or arbitrary markup
          inside them, and the logo rules that out. The page-flow content is given
          matching top and bottom padding (see orgChartPrint.css) so it never
          collides with either band.

          Deliberately NOT inside .oc-print-section: these must sit at the top
          level of the document so they are fixed against the PAGE, not against a
          section's own box. */}
      <div className="oc-print-runhead">
        <img className="oc-print-runhead-logo" src="/images/rtg-logo-red.png" alt="" />
        <span className="oc-print-runhead-title">{title}</span>
        <span className="oc-print-runhead-scope">{caption}</span>
      </div>

      <div className="oc-print-runfoot">
        <span className="oc-print-runfoot-left">Robusta Group, confidential</span>
        {printedOn && (
          <span className="oc-print-runfoot-mid">Printed {printedOn}</span>
        )}
        {/* The page number is drawn by the browser from a CSS counter, since the
            DOM cannot know how the content paginated. */}
        <span className="oc-print-runfoot-right" />
      </div>

      {/* First-page cover block. Distinct from the running header: this carries
          the full title and scope once, at the top of page one, while the running
          header is the compact strip that repeats. */}
      <div className="oc-print-doc-head">
        <img className="oc-print-doc-logo" src="/images/rtg-logo-red.png" alt="" />
        <h1 className="oc-print-doc-title">{title}</h1>
        <p className="oc-print-doc-sub">{caption}</p>
      </div>

      {sections.map((section) => (
        <section className="oc-print-section" key={section.id}>
          {/* The roots section has no manager to name: those nodes are the top of
              the hierarchy. Labelling it from the org would be wrong once the
              print is scoped to a department, so it states its role instead. */}
          <div className="oc-print-section-head">
            {section.manager
              ? section.manager.name
              : sections.length === 1 ? 'Organization' : 'Top of the organization'}
            {section.manager?.title && (
              <span className="oc-print-section-role">{section.manager.title}</span>
            )}
          </div>

          <div className="oc-print-row">
            {section.reports.map((r) => (
              <div className="oc-print-cell" key={r.id}>
                <OrgNodeCard node={r} variant="tree" isAdmin={isAdmin} />
              </div>
            ))}
          </div>

          {/* A section with enough reports to span a page break leaves the reader
              on the following page with no idea whose reports these are, since
              the header is back on the previous one. This repeats it as a footer.
              Only for sections big enough to actually break: on a short section
              it would be redundant noise. Print-only, like the rest of the doc. */}
          {section.manager && section.reports.length > 12 && (
            <p className="oc-print-section-cont">
              {section.reports.length} direct reports of {section.manager.name}
            </p>
          )}
        </section>
      ))}

      {sections.length === 0 && (
        <p className="oc-print-empty">Nothing to print for the current selection.</p>
      )}
    </div>
  );
}
